<template>
  <v-container>
    <v-row justify="center">
      <v-dialog v-model="dialog" v-show="showDialog" @click:outside="closeDialog" persistent max-width="1050px">
        <v-card>
          <v-form ref="form" lazy-validation @input="formToggled">
            <v-card-title class="subtitle-1 grey accent" primary-title>
              <span class="text-h5">Add WholeSale Product</span>
            </v-card-title>
            <v-card-text>
              <v-container>

                <!-- ─────────────────────────────────────────────── -->
                <!-- AI AUTOFILL SECTION -->
                <!-- ─────────────────────────────────────────────── -->
                <v-row>
                  <v-col cols="12">
                    <v-card outlined class="pa-4 mb-2" :class="aiMatchFound ? 'match-found-card' : 'upload-card'">
                      <!-- Match found warning -->
                      <v-alert v-if="aiMatchFound && aiExistingProduct" type="warning" dense class="mb-3">
                        ⚠️ This product already exists in the database!
                        <strong>{{ aiExistingProduct.SKU }}</strong> —
                        {{ aiExistingProduct.Title }}
                      </v-alert>

                      <!-- Upload area (only show when not loading) -->
                      <div v-if="!aiLoading" class="ai-upload-area" @click="$refs.aiImageInput.click()"
                        @dragover.prevent @drop.prevent="handleAiDrop">
                        <v-icon size="36" color="accent">mdi-camera-plus</v-icon>
                        <p class="mt-2 mb-0 grey--text">
                          📸 Upload product photos for AI autofill
                        </p>
                        <p class="caption grey--text">
                          Click or drag & drop up to 5 images
                        </p>
                        <input ref="aiImageInput" type="file" accept="image/*" multiple style="display: none"
                          @change="handleAiImageUpload" />
                      </div>

                      <!-- Loading state -->
                      <div v-if="aiLoading" class="text-center pa-4">
                        <v-progress-circular indeterminate color="accent" size="40" />
                        <p class="mt-3 grey--text">
                          🤖 AI is analyzing your images...
                        </p>
                      </div>

                      <!-- Uploaded image chips + add more -->
                      <div v-if="aiImages.length && !aiLoading" class="mt-3 d-flex flex-wrap align-center">
                        <v-chip v-for="(img, i) in aiImages" :key="i" class="ma-1" close small
                          @click:close="removeAiImage(i)">
                          {{ img.name }}
                        </v-chip>
                        <v-btn small text color="accent" class="ml-2" @click="$refs.aiImageInput.click()">
                          + Add more
                        </v-btn>
                        <v-btn small text color="grey" class="ml-1" @click="clearAiImages">
                          Clear
                        </v-btn>
                      </div>

                      <!-- Autofill success badge -->
                      <div v-if="aiAutofilled.length && !aiLoading && !aiMatchFound" class="mt-2">
                        <v-chip small color="success" text-color="white">
                          <v-icon small left>mdi-robot</v-icon>
                          AI autofilled: {{ aiAutofilled.join(', ') }}
                        </v-chip>
                        <span class="caption grey--text ml-2">
                          Yellow fields were filled by AI — please review
                        </span>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
                <!-- ─────────────────────────────────────────────── -->
                <!-- END AI AUTOFILL SECTION -->
                <!-- ─────────────────────────────────────────────── -->

                <v-row>
                  <v-col>
                    <TreeSelect v-model="product.Category" style="padding-top: 0px; margin-top: 0px; width: 100%"
                      :class="aiAutofilled.includes('Category') ? 'ai-autofilled' : ''" :dropdown-style="{
                        maxHeight: '400px',
                        overflow: 'auto',
                      }" :tree-data="categories" :replaceFields="{
                        children: 'children',
                        title: 'name',
                        key: 'id',
                        value: 'id',
                      }" :rules="[rules.required]" placeholder="Category" label="Category" filled color="accent"
                      size="large" @change="changeCategory" id="category">
                    </TreeSelect>
                  </v-col>
                  <v-col>
                    <v-autocomplete color="accent" label="Brand" v-model="product.Brand" type="text" :items="brands"
                      :rules="[rules.required]" :item-text="'name'" :item-value="'id'" outlined dense clearable
                      return-object multiple :class="aiAutofilled.includes('Brand') ? 'ai-autofilled' : ''"
                      @change="ChangeBrand">
                    </v-autocomplete>
                  </v-col>
                  <v-col>
                    <v-autocomplete color="accent" label="Gender" v-model="product.Gender" type="text" :items="genders"
                      :rules="[rules.required]" :item-text="'name'" :item-value="'id'" outlined dense clearable
                      return-object :class="aiAutofilled.includes('Gender') ? 'ai-autofilled' : ''">
                    </v-autocomplete>
                  </v-col>
                  <v-col>
                    <v-autocomplete color="accent" label="Season" v-model="product.Season" type="text" :items="seasons"
                      :item-text="'name'" :item-value="'id'" outlined dense clearable return-object
                      :class="aiAutofilled.includes('Season') ? 'ai-autofilled' : ''">
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-autocomplete color="accent" label="Product Type" v-model="product.ProductType" type="text"
                      :items="productsType" :rules="[rules.required]" :item-text="'name'" :item-value="'id'" outlined
                      dense clearable return-object @change="ChangeProductType">
                    </v-autocomplete>
                  </v-col>
                  <v-col>
                    <v-autocomplete color="accent" label="Product unit" v-model="product.productUnit" type="text"
                      :items="units" :rules="[rules.required]" :item-text="'name'" :item-value="'id'" outlined dense
                      clearable return-object>
                    </v-autocomplete>
                  </v-col>
                  <v-col>
                    <v-text-field color="accent" label="Bar Code" v-model="product.BarCode" type="text" outlined dense
                      clearable @wheel="$event.target.blur()">
                    </v-text-field>
                  </v-col>
                  <v-col>
                    <v-text-field color="accent" :label="product.productUnit == 'Kg'
                        ? 'Min order (Kg)'
                        : 'Min order (Pieces)'
                      " v-model="product.MinOrder" type="number" outlined dense min="1" clearable
                      :rules="[rules.required]" @wheel="$event.target.blur()">
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field color="accent" label="Gross Weight (KG)" v-model="product.GrossWeight" type="number"
                      outlined dense clearable :rules="[rules.required]" @wheel="$event.target.blur()">
                    </v-text-field>
                  </v-col>
                  <v-col>
                    <v-text-field color="accent" label="Net Weight (KG)" v-model="product.NetWeight" type="number"
                      outlined dense clearable :rules="[rules.required]" @wheel="$event.target.blur()">
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-expansion-panels class="margin">
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      Locations
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div v-for="(item, i) in product.Locations" :key="i" class="borderd">
                        <v-row>
                          <v-col>
                            <v-text-field color="accent" label="Location" v-model="product.Locations[i].location"
                              type="text" outlined dense clearable :rules="[rules.required]"
                              @wheel="$event.target.blur()">
                            </v-text-field>
                          </v-col>
                          <v-col>
                            <v-text-field color="accent" label="Quantity" v-model="product.Locations[i].quantity"
                              type="text" outlined dense clearable :rules="[rules.required]"
                              @wheel="$event.target.blur()">
                            </v-text-field>
                          </v-col>
                          <v-col cols="1" class="mt-1">
                            <v-icon class="ml-4" size="30" color="error" cursor="pointer"
                              @click="removeField(i, 'location')">mdi-close-thick</v-icon>
                          </v-col>
                        </v-row>
                      </div>
                      <v-btn @click="AddNewLocation"> Add location </v-btn>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
                <div v-if="SizeLine" class="margin">
                  <v-expansion-panels>
                    <v-expansion-panel>
                      <v-expansion-panel-header>Variations</v-expansion-panel-header>
                      <v-expansion-panel-content v-if="product.ProductType == 'Size Line'">
                        <div v-for="(item, i) in product.Variations" :key="i" class="borderd">
                          <v-row>
                            <v-col>
                              <v-autocomplete color="accent" label="Color" v-model="product.Variations[i].color"
                                type="text" :items="colors" :rules="[rules.required]" :item-text="'name'"
                                :item-value="'name'" outlined dense clearable>
                              </v-autocomplete>
                            </v-col>
                            <v-col cols="1" class="mt-1">
                              <v-icon class="ml-4" size="30" color="error" cursor="pointer"
                                @click="removeField(i, 'variation')">mdi-close-thick</v-icon>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col v-for="(conf, k) in sizeconfiguration" :key="k">
                              <v-text-field v-if="sizeconfiguration[k].type == 'string'" color="accent"
                                :label="sizeconfiguration[k].title" v-model="product.Variations[i].sizes[
                                  sizeconfiguration[k].title
                                  ]
                                  " type="text" outlined dense clearable :rules="[rules.required]"
                                @wheel="$event.target.blur()" />
                              <v-autocomplete v-if="sizeconfiguration[k].type == 'select'" color="accent"
                                :label="sizeconfiguration[k].title" v-model="product.Variations[i].sizes[
                                  sizeconfiguration[k].title
                                  ]
                                  " type="text" :items="sizeconfiguration[k].values" :rules="[rules.required]"
                                :item-text="'name'" :item-value="'id'" outlined dense clearable>
                              </v-autocomplete>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col>
                              <v-text-field color="accent" label="Min Quantity"
                                v-model="product.Variations[i].minQuantity" type="number" outlined dense clearable
                                :rules="[rules.required]" @wheel="$event.target.blur()">
                              </v-text-field>
                            </v-col>
                            <v-col>
                              <v-text-field color="accent" label="Max Quantity"
                                v-model="product.Variations[i].maxQuantity" type="number" outlined dense clearable
                                :rules="[rules.required]" @wheel="$event.target.blur()">
                              </v-text-field>
                            </v-col>
                          </v-row>
                        </div>
                        <v-btn @click="addItem">Add Variation</v-btn>
                      </v-expansion-panel-content>

                      <v-expansion-panel-content v-if="
                        product.ProductType == 'WH MIX' ||
                        product.ProductType == 'WH ITEMS' ||
                        product.ProductType == 'WH CR'
                      ">
                        <div v-for="(item, i) in product.Variations" :key="i" class="borderd">
                          <v-row>
                            <v-col>
                              <VTreeselect v-model="product.Variations[i].item" :multiple="false"
                                :options="variationCategory" @input="($event) => changeVariation($event, i)"
                                placeholder="item" search-nested :showCount="true" />
                            </v-col>
                            <v-col cols="1" class="mt-1">
                              <v-icon class="ml-4" size="30" color="error" cursor="pointer"
                                @click="removeField(i, 'variation')">mdi-close-thick</v-icon>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col v-for="(conf, k) in product.Variations[i].configuration" :key="k">
                              <v-text-field v-if="conf.type == 'string'" color="accent" :label="conf.title" v-model="product.Variations[i].sizes[conf.title]
                                " type="text" outlined dense clearable :rules="[rules.required]"
                                @wheel="$event.target.blur()" />
                              <v-autocomplete v-if="conf.type == 'select'" color="accent" :label="conf.title" v-model="product.Variations[i].sizes[conf.title]
                                " type="text" :items="conf.values" :rules="[rules.required]" :item-text="'name'"
                                :item-value="'id'" outlined dense clearable>
                              </v-autocomplete>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col>
                              <v-text-field color="accent" label="Min Quantity"
                                v-model="product.Variations[i].minQuantity" type="number" outlined dense clearable
                                :rules="[rules.required]" @wheel="$event.target.blur()">
                              </v-text-field>
                            </v-col>
                            <v-col>
                              <v-text-field color="accent" label="Max Quantity"
                                v-model="product.Variations[i].maxQuantity" type="number" outlined dense clearable
                                :rules="[rules.required]" @wheel="$event.target.blur()">
                              </v-text-field>
                            </v-col>
                          </v-row>
                        </div>
                        <v-btn @click="addItem">Add Variation</v-btn>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click="closeDialog">
                Close
              </v-btn>
              <v-btn color="blue darken-1" text @click="createProduct">
                Save
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>
    </v-row>

    <v-dialog v-model="UpdateTitle" v-show="showUpdateTitle" @click:outside="UpdateTitle == true" persistent
      max-width="600px">
      <v-card>
        <v-form ref="formUpdate" lazy-validation @input="formToggled">
          <v-card-title class="subtitle-1 grey accent" primary-title>
            <span class="text-h5">Review new product</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col>
                  <v-text-field color="accent" label="Title" v-model="newProduct.Title" type="text" outlined dense
                    clearable :rules="[rules.required]" @wheel="$event.target.blur()">
                  </v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="2">
                  <h3 class="text-grey">SKU :</h3>
                </v-col>
                <v-col>
                  <h4>{{ newProduct.SKU }}</h4>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="ShowPrintModal">
              print SKU
            </v-btn>
            <v-btn color="blue darken-1" text @click="saveTitle"> Save </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="PrintSKU" width="650px">
      <v-card>
        <v-form ref="form" lazy-validation @input="formToggled">
          <v-card-title class="subtitle-1 grey accent" primary-title>
            <span class="text-h5">Print the SKU</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-spacer></v-spacer>
              <v-row><v-col></v-col></v-row>
              <v-row>
                <h3>
                  How many time you want to print this SKU {{ currentSKU }}
                </h3>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field color="accent" label="" v-model="PrintNumber" type="number" outlined dense clearable
                    :rules="[rules.required, rules.prices]" @wheel="$event.target.blur()">
                  </v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="closeDialog">
              Close
            </v-btn>
            <v-btn color="blue darken-1" text @click="PrintTheSKU">
              Print
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="statusDialog" width="unset">
      <status-info :responseMessage="responseMessage" :filename="filename" :pending="pending" @close="closeStatusDialog"
        @showProductInfo="postSearch(product.SKU)" :textColor="textColor" :showCheckBtn="showCheckBtn"></status-info>
    </v-dialog>

    <v-snackbar top v-model="snackbar" color="#FF3232" class="white--text">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import StatusInfo from "../organisms/StatusInfo";
import { TreeSelect } from "ant-design-vue";
import { printerSKU } from "../../modules/print";
import VTreeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

// FIX: define the blank product shape once so we can reuse it for resetting
const defaultProduct = () => ({
  Category: undefined,
  BrandName: undefined,
  Gender: undefined,
  productUnit: undefined,
  Locations: [
    {
      location: null,
      quantity: null,
    },
  ],
});

export default {
  name: "create-wh-product",
  props: { close: Function },
  components: {
    StatusInfo,
    TreeSelect,
    VTreeselect,
  },
  data: () => ({
    dialog: false,
    showDialog: false,
    statusDialog: true,
    pending: true,
    filename: "",
    showCheckBtn: false,
    textColor: "",
    responseMessage: "",
    categories: [],
    SizeLine: false,
    brands: [],
    seasons: [],
    productsType: [],
    genders: [],
    colors: [],
    product: defaultProduct(),
    units: ["Pieces", "Kg"],
    rules: {
      required: (value) => !!value || "Field is required.",
    },
    snackbarText: "",
    snackbar: false,
    newProduct: {},
    UpdateTitle: false,
    showUpdateTitle: false,
    PrintNumber: 1,
    PrintSKU: false,
    currentSKU: "",
    sizeconfiguration: [],
    variationCategory: [],

    // ── AI autofill state ──
    aiLoading: false,
    aiImages: [],
    aiAutofilled: [],
    aiMatchFound: false,
    aiExistingProduct: null,
  }),

  watch: {
    product: {
      deep: true,
      handler(value) {
        // FIX: only persist to localStorage if there is meaningful data,
        // so an intentional reset doesn't immediately get written back
        if (value && value.Category) {
          localStorage.setItem("whProduct", JSON.stringify(value));
        }
      },
    },
  },

  mounted() {
    // FIX: restore from localStorage only if there is saved data
    const saved = localStorage.getItem("whProduct");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.Category) {
          this.product = parsed;
          this.SizeLine = true;
        }
      } catch (e) {
        // corrupted data — clear it and start fresh
        localStorage.removeItem("whProduct");
      }
    }
    this.statusDialog = true;
    this.pending = true;
    this.fetchDropDowns();
  },

  methods: {
    // ─────────────────────────────────────────
    // FIX: centralised reset so we never miss a field
    // ─────────────────────────────────────────
    resetForm() {
      // Clear localStorage first so the watcher doesn't re-save stale data
      localStorage.removeItem("whProduct");

      // Reset product to a fresh default object
      this.product = defaultProduct();

      // Reset UI flags
      this.SizeLine = false;
      this.sizeconfiguration = [];
      this.newProduct = {};
      this.PrintNumber = 1;
      this.currentSKU = "";

      // Reset AI autofill state
      this.aiLoading = false;
      this.aiImages = [];
      this.aiAutofilled = [];
      this.aiMatchFound = false;
      this.aiExistingProduct = null;

      // Reset form validation if the ref is available
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    // ─────────────────────────────────────────
    // AI AUTOFILL METHODS
    // ─────────────────────────────────────────
    handleAiDrop(e) {
      const files = Array.from(e.dataTransfer.files).filter((f) =>
        f.type.startsWith("image/")
      );
      if (files.length) this.runAiAutofill(files);
    },

    handleAiImageUpload(e) {
      const files = Array.from(e.target.files);
      if (files.length) this.runAiAutofill(files);
      e.target.value = "";
    },

    removeAiImage(index) {
      this.aiImages.splice(index, 1);
    },

    clearAiImages() {
      this.aiImages = [];
      this.aiAutofilled = [];
      this.aiMatchFound = false;
      this.aiExistingProduct = null;
    },

    async runAiAutofill(files) {
      this.aiImages = files;
      this.aiLoading = true;
      this.aiMatchFound = false;
      this.aiExistingProduct = null;
      this.aiAutofilled = [];

      try {
        const formData = new FormData();
        files.forEach((f) => formData.append("images", f));

        const result = await this.$store.dispatch("aiAutofill", formData);
        const data = result.data;

        if (data.isMatch && data.existingProduct) {
          this.aiMatchFound = true;
          this.aiExistingProduct = data.existingProduct;

          const ep = data.existingProduct;

          if (ep.CategoryId) {
            this.product.Category = ep.CategoryId;
            this.findConfig(this.categories, ep.CategoryId);
            this.aiAutofilled.push("Category");
          }

          if (ep.Brand) {
            const match = this.brands.find(
              (b) => b.name.toLowerCase() === ep.Brand.toLowerCase()
            );
            if (match) {
              this.product.Brand = [match];
              this.aiAutofilled.push("Brand");
            }
          }

          if (ep.GenderId) {
            const match = this.genders.find((g) => g.id === ep.GenderId);
            if (match) {
              this.product.Gender = match;
              this.aiAutofilled.push("Gender");
            }
          }

          if (ep.SeasonId) {
            const match = this.seasons.find((s) => s.id === ep.SeasonId);
            if (match) {
              this.product.Season = match;
              this.aiAutofilled.push("Season");
            }
          }

        } else if (data.autofill) {
          // existing autofill logic for new products...
        }
      } catch (e) {
        console.error("AI autofill failed:", e);
        this.snackbarText = "AI autofill failed, please fill manually.";
        this.snackbar = true;
      } finally {
        this.aiLoading = false;
      }
    },

    findCategoryByName(categories, name) {
      for (const cat of categories) {
        if (cat.name.toLowerCase().includes(name.toLowerCase())) return cat;
        if (cat.children?.length) {
          const found = this.findCategoryByName(cat.children, name);
          if (found) return found;
        }
      }
      return null;
    },

    // ─────────────────────────────────────────
    // EXISTING METHODS
    // ─────────────────────────────────────────
    ChangeBrand(value) {
      console.log(JSON.stringify(value));
      if (this.product.ProductType != "WH MIX") {
        this.product.Brand = [value[value.length - 1]];
      }
    },

    changeCategory(data) {
      console.log(data);
      console.log(this.categories);
      this.findConfig(this.categories, data);
      console.log(this.sizeconfiguration);
    },

    findConfig(data, id) {
      for (var i = 0; i < data.length; i++) {
        if (data[i].id == id) {
          var configuration = data[i].configuration.map((c) => {
            if (c.type == "select") {
              c.values = c.values.map((v) => {
                return { name: v, id: v };
              });
            }
            return c;
          });
          this.sizeconfiguration = configuration;
          return;
        } else if (data[i].children.length) {
          this.findConfig(data[i].children, id);
        } else {
          continue;
        }
      }
    },

    findVariationCat(data, id, index) {
      for (var i = 0; i < data.length; i++) {
        console.log("this is i ==>", i);
        if (data[i].id == id) {
          if (data[i].configuration) {
            var config = [];
            for (var k = 0; k < data[i].configuration.length; k++) {
              var c = data[i].configuration[k];
              var values = c.values;
              if (c.type == "select") {
                values = values.map((v) => {
                  return { name: v, id: v };
                });
              }
              config.push({ title: c.title, type: c.type, values });
            }
            this.product.Variations[index].configuration = config;
          } else {
            this.product.Variations[index].configuration = [];
          }
          this.product.Variations[index].name = data[i].label;
          return;
        } else if (data[i].children.length) {
          this.findVariationCat(data[i].children, id, index);
        } else {
          continue;
        }
      }
    },

    changeVariation(event, index) {
      this.SizeLine = false;
      this.findVariationCat(this.variationCategory, event, index);
      this.SizeLine = true;
      console.log(this.product.Variations);
    },

    async fetchDropDowns() {
      const response = await this.$store.dispatch("getDropdownsWholeSales");
      this.categories = response.data.categories;
      this.brands = response.data.brands;
      this.seasons = response.data.seasons;
      this.productsType = response.data.productsType;
      this.colors = response.data.colors;
      this.genders = response.data.genders;
      this.pending = false;
      this.statusDialog = false;
      this.showDialog = true;
      this.dialog = true;
      this.variationCategory = response.data.variationCategories;
    },

    // FIX: reset everything on close so next open starts fresh
    closeDialog() {
      this.resetForm();
      this.close();
      this.dialog = false;
      this.PrintSKU = false;
    },

    closeStatusDialog() {
      this.resetForm();
      this.close();
      this.dialog = false;
    },

    addItem() {
      this.SizeLine = false;
      if (this.product.ProductType == "Size Line") {
        this.product.Variations.push({
          color: null,
          sizes: {},
          minQuantity: null,
          maxQuantity: null,
        });
      } else {
        this.product.Variations.push({
          item: null,
          sizes: {},
          minQuantity: null,
          maxQuantity: null,
          configuration: [],
        });
      }
      this.SizeLine = true;
      console.log(JSON.stringify(this.product.Variations));
    },

    removeField(index, type) {
      if (type == "location") {
        this.product.Locations.splice(index, 1);
      }
      if (type == "variation") {
        this.SizeLine = false;
        this.product.Variations.splice(index, 1);
        this.SizeLine = true;
      }
    },

    ChangeProductType(data) {
      console.log(data);
      if (data == "Size Line") {
        this.SizeLine = true;
        this.product.Variations = [
          { color: null, sizes: {}, minQuantity: null, maxQuantity: null },
        ];
      } else if (
        data == "WH MIX" ||
        data == "WH ITEMS" ||
        data == "WH CR"
      ) {
        this.SizeLine = true;
        this.product.Variations = [
          {
            item: null,
            sizes: {},
            minQuantity: null,
            maxQuantity: null,
            configuration: [],
          },
        ];
      } else {
        this.SizeLine = false;
        delete this.product.Variations;
      }
    },

    async ShowPrintModal() {
      this.PrintSKU = true;
      this.currentSKU = this.newProduct.SKU;
      this.UpdateTitle = false;
    },

    PrintTheSKU() {
      printerSKU(this.PrintNumber, this.currentSKU);
      this.PrintSKU = false;
    },

    AddNewLocation() {
      this.product.Locations.push({ location: null, quantity: null });
    },

    formToggled() { },

    async createProduct() {
      console.table(JSON.stringify(this.product));
      var ErrorVariations = false;
      console.log(
        "this is the variations =>",
        JSON.stringify(this.product.Variations)
      );
      this.product.Variations.map((item) => {
        if (this.product.ProductType == "Size Line") {
          if (
            !item.color ||
            !Object.keys(item.sizes).length ||
            !item.minQuantity ||
            !item.maxQuantity
          ) {
            ErrorVariations = true;
          }
        } else {
          if (!item.item || !item.minQuantity || !item.maxQuantity) {
            ErrorVariations = true;
          }
        }
      });
      if (ErrorVariations) {
        this.snackbarText = "Please fix the variations !";
        this.snackbar = true;
        return;
      }
      if (
        (this.$refs.form.validate() && this.product.Category != undefined) ||
        this.product.Category != null ||
        !this.product.NetWeight ||
        !this.product.GrossWeight
      ) {
        this.pending = true;
        this.statusDialog = true;
        this.textColor = "basic--text";
        this.dialog = false;
        this.$store
          .dispatch("createProduct", this.product)
          .then((result) => {
            // FIX: clear localStorage after a successful save
            localStorage.removeItem("whProduct");
            this.newProduct = result.data;
            this.pending = false;
            this.statusDialog = false;
            this.showUpdateTitle = true;
            this.UpdateTitle = true;
          })
          .catch((error) => {
            console.log(error);
            this.pending = false;
            this.statusDialog = true;
            this.filename = "server_down.svg";
            this.responseMessage = error.response?.data;
          });
      } else {
        this.snackbarText = "The form is filled incorretly!";
        this.snackbar = true;
      }
    },

    async saveTitle() {
      if (this.$refs.formUpdate.validate()) {
        this.pending = true;
        this.statusDialog = true;
        this.textColor = "basic--text";
        this.showUpdateTitle = false;
        this.UpdateTitle = false;
        this.$store
          .dispatch("updateTitle", {
            id: this.newProduct.id,
            Title: this.newProduct.Title,
          })
          .then(() => {
            this.pending = false;
            this.statusDialog = true;
            this.responseMessage =
              "Product has been created ! SKU : " + this.newProduct.SKU;
            this.filename = "high_five.svg";
            // FIX: ensure localStorage is clean after the full save flow completes
            localStorage.removeItem("whProduct");
          })
          .catch(() => {
            this.pending = false;
            this.statusDialog = true;
            this.filename = "server_down.svg";
            this.responseMessage = "Something went wrong! Please try again!";
          });
      } else {
        this.snackbarText = "The form is filled incorretly!";
        this.snackbar = true;
      }
    },
  },
};
</script>

<style>
.add_wh_pr_modal {
  background-color: #fff !important;
}

div#text {
  margin-top: 48px;
  text-align: center;
}

div#dropzone {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999999999;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  transition: visibility 175ms, opacity 175ms;
  display: table;
  text-shadow: 1px 1px 2px #000;
  color: #fff;
  background: rgba(0, 0, 0, 0.45);
  font: bold 42px Oswald, DejaVu Sans, Tahoma, sans-serif;
}

div#textnode {
  display: table-cell;
  text-align: center;
  vertical-align: middle;
  transition: font-size 175ms;
}

.margin {
  margin-top: 25px;
}

.borderd {
  padding: 2em 1.5em;
  border-bottom: 2px solid lightgray;
  background-color: #f2f9ff;
  margin: 1em 0;
}

/* ── AI Autofill styles ── */
.ai-upload-area {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
}

.ai-upload-area:hover {
  border-color: var(--v-accent-base);
}

.upload-card {
  border-color: #e0e0e0 !important;
}

.match-found-card {
  border-color: #ff9800 !important;
  background-color: #fff8f0 !important;
}

.ai-autofilled .v-input__slot {
  background-color: #fffde7 !important;
}

.ai-autofilled {
  border-radius: 4px;
}
</style>